import { CircularProgress, Fade } from '@material-ui/core';
import classNames from 'classnames';
import React, {
  FC,
  KeyboardEvent,
  PointerEvent,
  Suspense,
  TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInterval } from 'usehooks-ts';

import { Image } from 'src/general/components/Image/Image';
import { ProductDetailsPayloadImages } from 'src/types/ProductDetail.type';

import CarSpinAnimation from '../CarSpinAnimation/CarSpinAnimation';
import { drawImageInCanvas, ensureValueInRange, loadImages } from './CarRotator.helpers';
import styles from './CarRotator.module.scss';
import { FeatureStar } from 'src/pdp/components/FeatureStar/FeatureStar';
import { HotspotsExteriorWithActive } from 'src/pdp/Exterior/Exterior';
import { animate } from 'framer-motion';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';
import { useTimeoutFn } from 'react-use';

const IMAGE_WIDTH = 1040;
const IMAGE_HEIGHT = Math.floor((IMAGE_WIDTH / 16) * 9);
const IMAGE_WIDTH_FULL = 2120;
const IMAGE_HEIGHT_FULL = Math.floor((IMAGE_WIDTH_FULL / 16) * 9);
const ROTATOR_SPEED_RATIO = 1.2;
const ROTATOR_SPEED_MILLISECONDS = 35;

interface Props {
  imagesData: Pick<ProductDetailsPayloadImages, '360doorsopen' | '360doorsclosed'>;
  displayMode: 'doorsOpen' | 'doorsClosed';
  isFullscreen?: boolean;
  hotspots?: HotspotsExteriorWithActive[];
  onClickHotspot?: (featureName: string, index: number) => void;
  visible?: boolean;
  selectedFrame?: number;
  disableCarSpinAnimation?: boolean;
  blendOptions?: {
    blendLeftAndRight: boolean;
  };
  hasPromo?: boolean;
  hasStatus?: boolean;
}

export const CarRotator: FC<Props> = ({
  imagesData,
  displayMode,
  isFullscreen,
  hotspots,
  onClickHotspot,
  visible = true,
  selectedFrame,
  disableCarSpinAnimation,
  blendOptions,
  hasPromo,
  hasStatus,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragStart = useRef<number>(0);
  const dragStartImageIndex = useRef<number>(selectedFrame ?? 0);
  const imageIndex = useRef<number>(selectedFrame ?? 0);
  const [isTouchScreen, setIsTouchScreen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(selectedFrame ?? 0);
  const [isRotating, setIsRotating] = useState<boolean>(false);
  const [rotations, setRotations] = useState<number>(0);
  const [hasAnimation, setHasAnimation] = useState<boolean>(true);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isImageLoaded, setIsImageLoaded] = useState<number | null>(null);
  const [imagesElements, setImagesElements] = useState<(HTMLImageElement | null)[]>([]);
  const [applyDraggingClass, setApplyDraggingClass] = useState(false);

  // Apply a small timeout to avoid disabling pointer events immediately after starting dragging
  const [_isReady, cancel, reset] = useTimeoutFn(() => {
    setApplyDraggingClass(true);
  }, 500);

  useEffect(() => {
    if (isDragging) {
      setApplyDraggingClass(false);
      reset();
    } else {
      cancel();
      setApplyDraggingClass(false);
    }
  }, [isDragging, reset, cancel]);

  const images = useMemo(() => {
    const imagesDataKey: keyof typeof imagesData = displayMode === 'doorsOpen' ? '360doorsopen' : '360doorsclosed';
    return imagesData[imagesDataKey].map((item) => item.url);
  }, [imagesData, displayMode]);

  const handleStartDrag = useCallback(
    (startDrag: number) => {
      setHasAnimation(false);
      setIsRotating(false);
      setIsDragging(true);
      setRotations((rotations) => rotations + 1);

      dragStart.current = startDrag;
      dragStartImageIndex.current = selectedImageIndex;
    },
    [isLoading, selectedImageIndex],
  );

  const handleDrag = useCallback(
    (currentXPercent: number) => {
      const indexOffset = (-currentXPercent * imagesElements.length * ROTATOR_SPEED_RATIO) % imagesElements.length;
      const newIndex = ensureValueInRange(Math.round(dragStartImageIndex.current + indexOffset), imagesElements.length);

      setSelectedImageIndex(newIndex);
    },
    [imagesElements, selectedImageIndex],
  );

  useEffect(() => {
    drawImageInCanvas(imagesElements[selectedImageIndex], canvasRef.current);
  }, [imagesElements, selectedImageIndex]);

  const handleEndDrag = useCallback(() => {
    let eventOption = 'PDP';
    if (isFullscreen) {
      if (disableCarSpinAnimation) {
        eventOption = 'HOTSPOTS';
      } else {
        eventOption = 'EXTERIOR';
      }
    }
    if (imageIndex.current !== selectedImageIndex && isDragging) {
      pushToDataLayer({
        event: 'pdp_gallery_interaction',
        value: {
          option: eventOption,
          type: 'SPIN',
          value: 'SPIN',
        },
      });
    }
    setIsDragging(false);
  }, [selectedImageIndex]);

  const animateToSelectedFrame = useCallback(() => {
    // Calculate the shortest path to the target
    let maxAllowed = imagesElements.length;
    const forwardDistance = (selectedFrame! - imageIndex.current + maxAllowed) % maxAllowed;
    const backwardDistance = (imageIndex.current - selectedFrame! + maxAllowed) % maxAllowed;

    // Determine the shortest distance and direction
    const shortestDistance = forwardDistance <= backwardDistance ? forwardDistance : -backwardDistance;
    const finalTarget = imageIndex.current + shortestDistance;
    animate(imageIndex.current, finalTarget, {
      duration: 0.3,
      stiffness: 300,
      damping: 20,
      mass: 1,
      onUpdate: (value) => {
        if (value) {
          let newIndex = ((Math.round(value) % maxAllowed) + maxAllowed) % maxAllowed;
          imageIndex.current = newIndex;
          setSelectedImageIndex(newIndex);

          drawImageInCanvas(imagesElements[newIndex], canvasRef.current);
        }
      },
    });
  }, [imagesElements, selectedFrame, imageIndex.current, setSelectedImageIndex, drawImageInCanvas, canvasRef.current]);

  useEffect(() => {
    if (selectedFrame !== undefined) {
      animateToSelectedFrame();
      setHasAnimation(false);
      setIsRotating(false);
    }
  }, [selectedFrame]);

  useEffect(() => {
    //   stop rotating if any active
    if (hotspots?.some((h) => h.active)) {
      setIsRotating(false);
      setHasAnimation(false);
    }
  }, [hotspots]);

  const handleClickStart = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      const startDrag = event.clientX - rect.left;
      handleStartDrag(startDrag);
    },
    [handleStartDrag],
  );

  const handleClickMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (isDragging) {
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const currentXDrag = event.clientX - rect.left;
        const currentXPercent = (currentXDrag - dragStart.current) / rect.width;
        handleDrag(currentXPercent);
      }
    },
    [isDragging, handleDrag],
  );

  const handleTouchStart = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const startDrag = event.targetTouches[0].clientX - rect.left;
      handleStartDrag(startDrag);
    },
    [handleStartDrag],
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const currentXDrag = event.targetTouches[0].clientX - rect.left;
      const currentXPercent = (currentXDrag - dragStart.current) / rect.width;
      handleDrag(currentXPercent);
    },
    [handleDrag],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const supportedKeys = ['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight', 'PageUp', 'PageDown'];
      if (supportedKeys.indexOf(event.key) < 0) {
        return;
      }
      event.preventDefault();

      let newIndex = imageIndex.current;
      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowLeft':
          newIndex += 1;
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          newIndex -= 1;
          break;
        case 'PageUp':
          newIndex += 3;
          break;
        case 'PageDown':
          newIndex -= 3;
          break;
        default:
          // Nothing to do
          break;
      }
      newIndex = ensureValueInRange(newIndex, imagesElements.length);

      if (newIndex !== imageIndex.current) {
        imageIndex.current = newIndex;
        setIsDragging(true);
        setHasAnimation(false);
        setIsImageLoaded(null);
        drawImageInCanvas(imagesElements[imageIndex.current], canvasRef.current);
      }
    },
    [imagesElements],
  );

  const numberInterval = useMemo(() => {
    if (!isLoading && rotations === 0) {
      return ROTATOR_SPEED_MILLISECONDS;
    }
    return null;
  }, [isLoading, rotations]);

  useEffect(() => {
    setIsTouchScreen(!!('ontouchstart' in window));
  }, []);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    // Load images
    loadImages(images, IMAGE_WIDTH).then((imagesElements) => {
      if (isMounted && visible) {
        drawImageInCanvas(imagesElements[imageIndex.current], canvasRef.current);
        setImagesElements(imagesElements);
        setIsLoading(false);
        setHasAnimation(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [images, visible]);

  useEffect(() => {
    if (!visible) {
      setHasAnimation(false);
      setIsRotating(false);
      setIsDragging(false);
    }
  }, [visible]);

  // Rotation animation
  useInterval(() => {
    if (hotspots?.some((h) => h.active)) {
      setIsRotating(false);
      setSelectedImageIndex(imageIndex.current);
      return;
    }
    const newIndex = ensureValueInRange(imageIndex.current - 1, images.length);
    imageIndex.current = newIndex;
    if (newIndex !== 0) {
      setIsRotating(true);
      drawImageInCanvas(imagesElements[imageIndex.current], canvasRef.current);
      setSelectedImageIndex(imageIndex.current);
    } else {
      setIsRotating(false);
      setSelectedImageIndex(imageIndex.current);
      setRotations((rotations) => rotations + 1);
    }
  }, numberInterval);

  const displayAnimationBox = isLoading || hasAnimation;

  const handleClickHotspot = useCallback(
    (name: string, index: number) => {
      if (onClickHotspot) {
        onClickHotspot(name, index);
      }
    },
    [hotspots, onClickHotspot],
  );

  if (images.length === 0) {
    return null;
  }

  if (!visible) {
    return null;
  }

  return (
    <figure
      className={classNames(styles.root, {
        [styles.isFullscreen]: isFullscreen,
      })}
      onPointerDown={!isTouchScreen ? handleClickStart : undefined}
      onPointerUp={!isTouchScreen ? handleEndDrag : undefined}
      onPointerOut={!isTouchScreen ? handleEndDrag : undefined}
      onPointerMove={!isTouchScreen ? handleClickMove : undefined}
      onTouchStart={isTouchScreen ? handleTouchStart : undefined}
      onTouchEnd={isTouchScreen ? handleEndDrag : undefined}
      onTouchMove={isTouchScreen ? handleTouchMove : undefined}
      onKeyDown={handleKeyDown}
      onKeyUp={handleEndDrag}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="spinbutton"
      tabIndex={0}
      aria-label="Car 360 degree view"
      aria-valuenow={selectedImageIndex}
      aria-valuemin={0}
      aria-valuemax={images.length}
    >
      {!disableCarSpinAnimation && (
        <Fade in={displayAnimationBox} unmountOnExit>
          <div
            className={classNames(styles.animationWrapper, {
              [styles.withBadge]: !!hasPromo || !!hasStatus,
              [styles.withBlend]: !!blendOptions,
            })}
          >
            <Fade in={isLoading} unmountOnExit>
              <div className={styles.loader}>
                <CircularProgress />
              </div>
            </Fade>

            {!isLoading && (
              <Suspense fallback={null}>
                <CarSpinAnimation
                  className={styles.spinAnimation}
                  totalLoops={1}
                  onComplete={() => {
                    setHasAnimation(false);
                  }}
                />
              </Suspense>
            )}
          </div>
        </Fade>
      )}
      <div
        className={classNames(styles.blendContainer, {
          [styles.blend]: blendOptions !== undefined,
          [styles.blendLeftAndRight]: blendOptions?.blendLeftAndRight,
        })}
      >
        <canvas className={styles.canvas} ref={canvasRef} />

        <Image
          className={classNames(styles.image, {
            [styles.imageInactive]:
              (isDragging || isImageLoaded !== selectedImageIndex || isRotating) && (!isLoading || isRotating),
          })}
          src={images[selectedImageIndex]}
          width={isFullscreen ? IMAGE_WIDTH_FULL : IMAGE_WIDTH}
          height={isFullscreen ? IMAGE_HEIGHT_FULL : IMAGE_HEIGHT}
          alt=""
          loading={selectedImageIndex === 0 ? 'eager' : undefined}
          onLoad={() => {
            setIsImageLoaded(selectedImageIndex);
          }}
          key={images[selectedImageIndex]}
          priority
          sizes="(max-width: 1136px) 100vw, 75vw"
        />
      </div>

      <div className={styles.pointsContainer}>
        {(hotspots ?? []).flatMap((hotspot, hotspotIndex) => {
          return hotspot.points
            .filter((point) => point.frame === selectedImageIndex)
            .map((point) => (
              <div
                data-testid={'feature-star-container'}
                key={`${hotspot.name}-${selectedImageIndex}`}
                style={{
                  position: 'absolute',
                  left: `${point.x * 100}%`,
                  top: `${point.y * 100}%`,
                }}
              >
                <FeatureStar
                  className={classNames(styles.featureStar, {
                    [styles.isDragging]: isDragging && applyDraggingClass,
                  })}
                  onClick={() => {
                    setIsDragging(false);
                    handleClickHotspot(hotspot.name, hotspotIndex);
                  }}
                  active={hotspot.active ?? false}
                  label={hotspot.name}
                />
              </div>
            ));
        })}
      </div>
    </figure>
  );
};
